<template>
    <div>
        <BasePageHeader title="Your collaboration profile" subtitle="Help others understand how to work with you effectively." />
        <div class="@container">
            <div class="flex items-baseline justify-between gap-4 mb-2 @max-xl:flex-wrap @max-xl:gap-1.5">
                <h2 class="scaffold-body">Here&rsquo;s how Nadia coaches your teammates about you when you create a profile.</h2>
                <BaseButton theme="tertiary" size="sm" class="animate-shadow-pulse" @click="isArcadeOpen = true"> See it in action &#9656; </BaseButton>
            </div>
            <div class="grid grid-cols-1 @xl:grid-cols-2 rounded-lgr shadow-lg bg-white">
                <div class="border flex flex-col">
                    <div class="px-6 py-4 animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.header }">
                        <h3 class="inline-block bg-valence-grey-50 rounded-lgr px-3 py-1 body-small">Without your profile</h3>
                    </div>
                    <div class="flex flex-col gap-3 px-8 py-6 border-t border-valence-grey-200 h-full">
                        <div class="self-end flex items-center gap-2 body-small text-valence-grey-750 animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.userMessage }">
                            <PerspectiveDot dimension="SN" size="md" />
                            John asks
                        </div>
                        <div class="self-end bg-valence-grey-50 rounded-lgr px-4 py-2 body animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.userMessage }">
                            How should I give {{ firstName }} feedback?
                        </div>
                        <p class="nadia-chat animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.nadiaResponse }">
                            Be direct and specific about what needs to change going forward.
                        </p>
                        <p class="scaffold-details-small text-valence-grey-750 mt-auto animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.footer }">
                            None of your own context to draw on
                        </p>
                    </div>
                </div>

                <div class="border flex flex-col">
                    <div class="px-6 py-4 animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.header }">
                        <h3 class="inline-block bg-valence-primary-200 rounded-lgr px-3 py-1 body-small">With your profile</h3>
                    </div>
                    <div class="flex flex-col gap-3 px-8 py-6 border-t border-valence-grey-200 h-full">
                        <div class="self-end flex items-center gap-2 body-small text-valence-grey-750 animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.userMessage }">
                            <PerspectiveDot dimension="SN" size="md" />
                            John asks
                        </div>
                        <div class="self-end bg-valence-grey-50 rounded-lgr px-4 py-2 body animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.userMessage }">
                            How should I give {{ firstName }} feedback?
                        </div>
                        <p class="nadia-chat animate-stagger-in" :style="{ '--stagger-delay': staggerDelays.nadiaResponse }">
                            Make sure
                            <span
                                class="inline-flex items-center gap-1 align-bottom bg-valence-grey-50 rounded px-1.5 py-0.5 body-small-bold animate-shadow-pulse"
                                :style="{
                                    '--shadow-pulse-color': 'var(--color-perspective-ei)',
                                    '--shadow-pulse-iterations': '1',
                                    animationDelay: staggerDelays.namePulseDelay,
                                }">
                                <PerspectiveDot dimension="EI" size="sm" />
                                {{ firstName }}
                            </span>
                            understands the intent behind the feedback first. They're far more open to pushback once they feel heard. Be specific, and anchor it in a real example.
                        </p>
                        <p
                            class="scaffold-details-small text-valence-primary-500 mt-auto flex items-center gap-1 animate-stagger-in"
                            :style="{ '--stagger-delay': staggerDelays.footer }">
                            &#10022; Example &middot; drawn from your profile once you create it
                        </p>
                    </div>
                </div>
            </div>
            <div class="flex flex-col items-end mt-4 gap-3">
                <template v-if="hasPerspectiveReport">
                    <div class="flex items-center justify-end gap-3">
                        <p class="scaffold-details-small md:scaffold-body text-valence-grey-750">
                            <span class="text-valence-primary-500">&#10022;</span>&nbsp; Nadia has drafted your profile from your Perspective results.
                        </p>
                        <BaseButton theme="primary" class="@max-xl:hidden" @click="handleCtaClick(() => handleCreateProfile())">See your draft profile</BaseButton>
                        <BaseButton theme="primary" size="sm" class="@xl:hidden" @click="handleCtaClick(() => handleCreateProfile())">See your draft profile</BaseButton>
                    </div>
                    <button v-if="documentUploadEnabled" class="scaffold-details-small md:scaffold-body-small text-valence-grey-750 hover:underline" @click="handleUpload">
                        Skip and upload documents instead
                    </button>
                </template>
                <div v-else-if="perspectiveAssessmentEnabled" class="flex flex-col gap-3">
                    <BaseButton theme="primary" @click="handleCtaClick(navigateToPerspective)">Start your profile</BaseButton>
                    <button v-if="documentUploadEnabled" class="scaffold-details-small md:scaffold-body-small text-valence-grey-750 hover:underline" @click="handleUpload">
                        Upload documents instead
                    </button>
                </div>
                <div v-else-if="perspectiveReportEnabled" class="flex flex-col gap-3">
                    <a class="base-btn-primary" target="_blank" rel="noopener noreferrer" :href="`${page.props.align_host}/perspective/questions`" @click="handleCtaClick()">
                        Start your profile
                    </a>
                    <button v-if="documentUploadEnabled" class="scaffold-details-small md:scaffold-body-small text-valence-grey-750 hover:underline" @click="handleUpload">
                        Upload documents instead
                    </button>
                </div>
                <div v-else class="flex flex-col gap-3">
                    <BaseButton theme="primary" data-testid="profile-create-cta" @click="handleCtaClick(handleUpload)">Create Profile</BaseButton>
                    <button class="scaffold-details-small md:scaffold-body-small text-valence-grey-750 hover:underline" @click="isSkipUploadDialogOpen = true">
                        Skip and fill out manually
                    </button>
                </div>
            </div>
        </div>
        <UploadDocumentDialog
            v-model:open="isUploadDialogOpen"
            :document-upload-enabled="documentUploadEnabled"
            :free-text-enabled="freeTextEnabled"
            @create-profile="handleCreateProfile" />
        <ConfirmationDialog
            v-model:open="isSkipUploadDialogOpen"
            title="Fill Out Your Profile Manually?"
            text="This will generate a blank collaboration profile. You can manually enter information or add more sources to base your collaboration profile from."
            confirm-label="Fill Out Manually"
            container-class="md:w-125"
            @confirm="handleSkip">
        </ConfirmationDialog>
        <DemoModal v-model:open="isArcadeOpen" :demo-url="COLLAB_PROFILE_DEMO_URL" header-text="Creating your profile takes about a minute" />
    </div>
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import PerspectiveDot from "~vue/Perspective/PerspectiveDot.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, onMounted, type Ref, ref } from "vue";

import ConfirmationDialog from "../ConfirmationDialog.vue";
import DemoModal from "../DemoModal.vue";
import UploadDocumentDialog from "./UploadDocumentDialog.vue";

const COLLAB_PROFILE_DEMO_URL = "https://demo.arcade.software/uz3KgHLOvPgO6XsujHWv?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true";

const userDetails = inject<Ref<UserDetails>>("user_details");
const firstName = computed(() => userDetails?.value.first_name ?? "you");

const staggerDelays = {
    header: "0s",
    userMessage: "0.15s",
    nadiaResponse: "0.3s",
    footer: "0.45s",
    namePulseDelay: "1.1s",
};

type ProfileCreationStartProps = {
    hasPerspectiveReport: boolean;
    perspectiveAssessmentEnabled?: boolean;
    perspectiveReportEnabled?: boolean;
    documentUploadEnabled?: boolean;
};

const {
    hasPerspectiveReport = false,
    perspectiveAssessmentEnabled = false,
    perspectiveReportEnabled = false,
    documentUploadEnabled = false,
} = defineProps<ProfileCreationStartProps>();

const freeTextEnabled = computed(() => !hasPerspectiveReport && !perspectiveAssessmentEnabled && !perspectiveReportEnabled);

onMounted(() => {
    void logUserInteraction(AmplitudeEventNames.COLLABORATION_PROFILE_CREATION_VIEWED, {
        has_perspective_report: hasPerspectiveReport,
    });
});

const handleCtaClick = (callback?: () => void) => {
    void logUserInteraction(AmplitudeEventNames.COLLABORATION_PROFILE_CREATION_CLICKED, {
        has_perspective_report: hasPerspectiveReport,
    });
    callback?.();
};

const page = usePage();

const isUploadDialogOpen = ref<boolean>(false);
const handleUpload = () => {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_UPLOAD_DOCUMENT_CLICKED, {
        has_perspective_report: hasPerspectiveReport,
        perspective_assessment_enabled: perspectiveAssessmentEnabled,
        perspective_report_enabled: perspectiveReportEnabled,
    });
    isUploadDialogOpen.value = true;
};

const isArcadeOpen = ref(false);

const emit = defineEmits<{
    createProfile: [fileIds?: number[], freeText?: string];
}>();

const navigateToPerspective = () => {
    router.visit("/perspective");
};

const handleCreateProfile = (fileIds?: number[], freeText?: string) => {
    emit("createProfile", fileIds, freeText);
};

const isSkipUploadDialogOpen = ref<boolean>(false);
const handleSkip = () => {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_SKIP_DOCUMENT_UPLOAD);
    emit("createProfile");
};
</script>
